import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, User, Link2, FileText, Library, PlayCircle, BarChart3, Wand2, TableProperties, ShieldCheck } from 'lucide-react';

import ProfileTab from '@/components/tabs/ProfileTab';
import BrokerTab from '@/components/tabs/BrokerTab';
import StrategiesTab from '@/components/tabs/StrategiesTab';
import LibraryTab from '@/components/tabs/LibraryTab';
import DeployTab from '@/components/tabs/DeployTab';
import AnalyticsTab from '@/components/tabs/AnalyticsTab';
import OptionWizardTab from '@/components/tabs/OptionWizardTab';
import ExecuteTradeTab from '@/components/tabs/ExecuteTradeTab';
import Sheet2Tab from '@/components/tabs/Sheet2Tab';
import AdminDashboard from '@/components/admin/AdminDashboard';

const ClientDashboard = ({ defaultTab = 'strategies' }) => {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(defaultTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            TukTuk Trading
          </h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-700 font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-gray-700 font-medium">{user?.name}</span>
            </div>
            <Button onClick={logout} variant="outline" size="sm" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/80 backdrop-blur-md p-1 rounded-xl shadow-sm border border-gray-200">
            <TabsTrigger value="profile" data-testid="profile-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="brokers" data-testid="brokers-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <Link2 className="w-4 h-4 mr-2" />
              Brokers
            </TabsTrigger>
            <TabsTrigger value="strategies" data-testid="strategies-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <FileText className="w-4 h-4 mr-2" />
              My Strategies
            </TabsTrigger>
            <TabsTrigger value="library" data-testid="library-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <Library className="w-4 h-4 mr-2" />
              Library
            </TabsTrigger>
            <TabsTrigger value="deploy" data-testid="deploy-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <PlayCircle className="w-4 h-4 mr-2" />
              Deploy
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="wizard" data-testid="wizard-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <Wand2 className="w-4 h-4 mr-2" />
              Option Wizard
            </TabsTrigger>
            <TabsTrigger value="execute" data-testid="execute-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <TableProperties className="w-4 h-4 mr-2" />
              Execute Trade
            </TabsTrigger>

            <TabsTrigger value="sheet2" data-testid="sheet2-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
              <TableProperties className="w-4 h-4 mr-2" />
              Sheet 2
            </TabsTrigger>

            {user?.role === 'admin' && (
              <TabsTrigger value="admin" data-testid="admin-tab" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Admin
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab />
          </TabsContent>

          <TabsContent value="brokers">
            <BrokerTab />
          </TabsContent>

          <TabsContent value="strategies">
            <StrategiesTab />
          </TabsContent>

          <TabsContent value="library">
            <LibraryTab />
          </TabsContent>

          <TabsContent value="deploy">
            <DeployTab />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab />
          </TabsContent>

          <TabsContent value="wizard">
            <OptionWizardTab />
          </TabsContent>

          <TabsContent value="execute">
            <ExecuteTradeTab />
          </TabsContent>

          <TabsContent value="sheet2">
            <Sheet2Tab />
          </TabsContent>

          {user?.role === 'admin' && (
            <TabsContent value="admin">
              <AdminDashboard />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDashboard;
